import Head from 'next/head';
import { Fragment, useEffect, useMemo, useRef } from 'react';

import { usePageContext } from '@/features/page/context';
import { usePageTitle } from '@/hooks/usePageTitle';
import { injectBodyElements, injectHeadElements } from '@/utils/html';

import { Page } from '../types';

const PageHead = ({ title, meta, isHomepage = false }: Pick<Page, 'title' | 'meta'> & { isHomepage?: boolean }) => {
  const injectedRef = useRef(false);

  const metaTitle = meta?.metaTitle ?? meta?.ogTitle;
  const metaDescription = meta?.metaDescription ?? meta?.ogDescription;
  const ogTitle = meta?.ogTitle ?? meta?.metaTitle;
  const ogDescription = meta?.ogDescription ?? meta?.metaDescription;
  const ogUrl = meta?.ogUrl;
  const ogImage = meta?.ogImage ?? meta?.featuredImage;
  const ogType = meta?.ogType ?? 'website';

  const { generalSettings } = usePageContext();

  title = useMemo(() => {
    if (metaTitle && metaTitle.length > 0) return metaTitle;

    return title;
  }, [metaTitle, title]);

  title = usePageTitle(title, !isHomepage, generalSettings?.site_name);

  useEffect(() => {
    if (!injectedRef.current && generalSettings) {
      injectHeadElements(generalSettings?.public_area_head_scripts);
      injectBodyElements(generalSettings?.public_area_body_scripts);
      injectedRef.current = true;
    }
  }, [generalSettings]);

  // useEffect(() => {
  //   const loadCSS = (href: string, id: string) => {
  //     if (document.getElementById(id)) return;
  //
  //     const link = document.createElement('link');
  //     link.rel = 'stylesheet';
  //     link.href = href;
  //     link.id = id;
  //     document.head.appendChild(link);
  //   };
  //
  //   // Load CSS immediately if the page is not being loaded from the bfcache.
  //   if (!window.performance || window.performance.navigation.type !== window.performance.navigation.TYPE_BACK_FORWARD) {
  //     loadCSS(baseContentLayerStylesUrl, baseContentLayerStylesId);
  //     loadCSS(contentLayerStylesUrl, contentLayerStylesId);
  //   }
  //
  //   // Load CSS when the page is being loaded from the bfcache.
  //   window.addEventListener('pageshow', event => {
  //     if (event.persisted) {
  //       loadCSS(baseContentLayerStylesUrl, baseContentLayerStylesId);
  //       loadCSS(contentLayerStylesUrl, contentLayerStylesId);
  //     }
  //   });
  // }, []);

  return (
    <Head>
      <meta charSet="utf-8" />
      {/*<meta name="viewport" content="width=device-width, initial-scale=1" />*/}
      {/* region Meta tags */}
      <Fragment>
        <title>{title}</title>
        <meta name="title" content={metaTitle || title || ''} />
      </Fragment>
      <meta name="description" content={metaDescription || ''} />
      {ogTitle && (
        <>
          <meta property="og:title" content={ogTitle} />
          <meta name="twitter:title" content={ogTitle} />
        </>
      )}
      {ogDescription && (
        <>
          <meta property="og:description" content={ogDescription} />
          <meta name="twitter:description" content={ogDescription} />
        </>
      )}
      {ogImage && (
        <>
          <meta name="twitter:card" content="summary_large_image" />
          <meta property="og:image" content={ogImage} />
          <meta name="twitter:image" content={ogImage} />
        </>
      )}
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      {ogType && <meta property="og:type" content={ogType} />}
      {/* endregion */}
      {/* eslint-disable-next-line */}
      {/*/!* eslint-disable-next-line *!/*/}
      {/*<link rel="stylesheet" href="/compiled-styles/base-content-layer-styles.css" />*/}
      {/*/!* eslint-disable-next-line *!/*/}
      {/*<link rel="stylesheet" href="/compiled-styles/content-layer-styles.css" />*/}

      {/* region PWA */}
      {generalSettings?.logo_mark && <link rel="shortcut icon" href={generalSettings.logo_mark} />}

      <meta name="apple-mobile-web-app-title" content={generalSettings?.site_name} />
      <meta name="application-name" content={generalSettings?.site_name} />
      <meta name="msapplication-TileColor" content="#ffffff" />
      <meta name="msapplication-config" content="/browserconfig.xml" />
      <meta name="theme-color" content="#ffffff" />
      {/* endregion */}
    </Head>
  );
};

export default PageHead;
